import React, { useState } from 'react';
import axios from 'axios';
import { getFunnel } from '../utils/tracking';

import RegisterProfile from '../components/RegisterProfile';
import RegisterSocial from '../components/RegisterSocial';
import { useParams, useLocation } from 'react-router-dom';
import { useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { GoogleLogin, GoogleOAuthProvider } from '@react-oauth/google';
import { Container } from 'react-bootstrap';
import RegisterCheckout from '../components/RegisterCheckout';
import { ProfileProvider } from "../contexts/ProfileContext"; // Import ProfileProvider
import { useLang, useLanguage } from "../contexts/LanguageContext";
import ProfileSwitcher from '../components/ProfileSwitcher';
import Testimonials from '../components/Testimonials';
import Upsell from '../components/Upsell';
import FAQ from '../components/FAQ';
import Navbar from '../components/Navbar';

const Register = () => {
	const [loggedIn, setLoggedIn] = useState(false);
	const API_URL = "https://api.viralgpt.com/auth";
    const navigate = useNavigate(); // For navigation after login
    const { lang } = useLang();
    const L = useLanguage();
	const location = useLocation(); // Move location hook to top

	const [profile, setProfile] = useState(null);
	const [error, setError] = useState("");
	const [loading, setLoading] = useState(true);
	const [isExistingUser, setIsExistingUser] = useState(false);

	// Get URL parameters
	const queryParams = new URLSearchParams(location.search);
	const purchasePlan = queryParams.get('purchase');
	const interval = queryParams.get('interval');
	const firstPackageInterval = queryParams.get('firstPackageInterval');


	// Store URL parameters in state 
	const [urlParams] = useState({
		purchasePlan,
		interval
	});

	useEffect(() => {
		
		const params = new URLSearchParams(location.search);
		const purchasePlan = params.get('purchase');
		const interval = params.get('interval');

		if (localStorage.getItem("accessToken")){
			fetchProfileInfo();
			return;
		}

		if (!purchasePlan && !interval) {
			navigate('/pricing');
			
		} else if (purchasePlan && interval) {
			fetchProfileInfo();
			var amount = 0;
			if (purchasePlan == "pro") {
				if (interval == "monthly") {
					amount = 69;
				} else if (interval == "annual") {
					amount = 41 * 12;
				}
			} else if (purchasePlan == "basic") {
				if (interval == "monthly") {
					amount = 49;
				} else if (interval == "annual") {
					amount = 29 * 12;
				}
			} else if (purchasePlan == "starter") {
				if (interval == "monthly") {
					amount = 9;
				} else if (interval == "annual") {
					amount = 60;
				}
			}
			axios.post("https://api.viralgpt.com/pixel", {
				event: "InitiateCheckout",
				funnel: getFunnel(),
				price: amount
			}, {
				withCredentials: true
			  })
		}
	}, [navigate, location]);


	const fetchProfileInfo = async () => {
		try {
		  const accessToken = localStorage.getItem("accessToken");
		  if (!accessToken) throw new Error("No access token found. Please log in.");
	
		  const response = await fetch(`https://api.viralgpt.com/auth/profile`, {
			method: "GET", // Use POST method to fetch profile data
			headers: {
			  "Content-Type": "application/json",
			  Authorization: `Bearer ${accessToken}`,
			},
		  });
	
		  if (!response.ok) {
			throw new Error("Failed to fetch profile info.");
		  }

	
		  const profileData = await response.json();
		  setProfile(profileData.profile);
		  if (profileData.profile.email) {
			setEmail(profileData.profile.email);
		  }

			// Same rule as the dashboard guard: anyone who has already paid belongs
			// in the dashboard, not back in a signup flow. One-time buyers carry no
			// planName, so checking that alone sent paying customers to /pricing.
			const hasLegacyPlan = Object.values(profileData.profile.socials || {}).some(
				(s) => s.planName === "pro" || s.planName === "basic" || s.planName === "starter"
			);
			if (
				hasLegacyPlan ||
				profileData.profile.hasActiveSubscription ||
				profileData.profile.hasBalanceMembership ||
				(profileData.profile.purchases || 0) > 0 ||
				(profileData.profile.topups || 0) > 0
			) {
				navigate("/dashboard");
				return;
			}
			
			if (!purchasePlan && !interval){
				navigate("/pricing");
				return;
			}
			if (profileData.profile.selectedProfile){
				setStep(3);
			} else if (!profileData.profile.selectedProfile){
				setStep(2);
			}
		  
		  setError(""); // Clear any previous errors
		  return 1
		} catch (err) {
		  console.error(err);
		  localStorage.removeItem("accessToken");
		  
		  
		  setError(L.register.profileLoadError);
		} finally {
		  setLoading(false);
		}
	  };

  

	// Validate the token
	const validateToken = async () => {
	  const accessToken = localStorage.getItem("accessToken");

	  try {
		if (!accessToken) return;
		const response = await fetch(`${API_URL}/validate`, {
		  method: "POST",
		  headers: {
			"Content-Type": "application/json",
		  },
		  body: JSON.stringify({ token: accessToken }),
		});
  
		if (!response.ok) {
		  console.warn("Token is invalid or expired. Redirecting to login...");
		  localStorage.removeItem("accessToken");
	  	
  
		} else {

		  setLoggedIn(true);
		  // Token was valid on mount — user already exists; suppress duplicate CompleteRegistration/InitiateCheckout pixels later in the wizard
		  setIsExistingUser(true);
		}
	  } catch (err) {
		console.error("Failed to validate token:", err);
		localStorage.removeItem("accessToken");
	  		
  
	  }
	};
  
	// Check token validity on component mount
	useEffect(() => {
	  validateToken();
	}, []);

  const { plan } = useParams(); 
  const [step, setStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');

  const [upsellChoices, setUpsellChoices] = useState([]);

  // Let the user go back to Step 1 to change their email
  const handleChangeEmail = () => {
    localStorage.removeItem("accessToken");
    setProfile(null);
    setStep(1);
    setEmail('');
  };

  // This callback is passed down and called after the user finishes step 1

  const stepOneComplete = async (emailForm, passwordForm) => {
    setEmail(emailForm);

    try {
	  if (emailForm.credential){
		const response = await axios.post('https://api.viralgpt.com/auth/google', { token: emailForm.credential, purchasePlan, language: lang });

		if (response.data.existing) {
			setIsExistingUser(true);
		}

		// Store the JWT in localStorage or context
		localStorage.setItem('accessToken', response.data.accessToken);

		// Redirect user after successful login
		setStep(2);
		await fetchProfileInfo();
		return;
	  }



      const response = await axios.post('https://api.viralgpt.com/auth/register', {
        email: emailForm,
        password: 'no-password',
        purchasePlan,
        language: lang,
      }, {
        withCredentials: true
      });

      console.log(response.data);

      const { accessToken, existing } = response.data;

	  if (existing) {
		setIsExistingUser(true);
	  } else {
		const pixelData = {
			event: 'CompleteRegistration',
			funnel: getFunnel(),
			email: emailForm
		}
		axios.post('https://api.viralgpt.com/pixel', pixelData, {
			withCredentials: true
		}).catch(err => console.error('Error sending pixel data:', err));
	  }

      // Save tokens and navigate to the dashboard
      localStorage.setItem("accessToken", accessToken);
	  
      setStep(2);
	  await fetchProfileInfo();

    } catch (error) {
      if (error.response && error.response.status === 400) {
        alert(error.response.data.error);
      } else {
        console.error('Error registering:', error);
      }
    }
  };


  const stepTwoComplete = async () => {
	if (purchasePlan && interval) {
		// Ensure a profile is selected before moving to the upsell step
		const accessToken = localStorage.getItem("accessToken");
		if (accessToken) {
			try {
				const res = await fetch('https://api.viralgpt.com/auth/profile', {
					headers: { Authorization: `Bearer ${accessToken}` }
				});
				const data = await res.json();
				if (!data.profile.selectedProfile && data.profile.socials) {
					const firstProfileId = Object.keys(data.profile.socials)[0];
					if (firstProfileId) {
						await axios.post("https://api.viralgpt.com/auth/select", {
							profileid: firstProfileId
						}, {
							headers: { Authorization: `Bearer ${accessToken}` }
						});
					}
				}
			} catch (err) {
				console.error("Error selecting profile:", err);
			}
		}
		await fetchProfileInfo();
		setStep(3);
		return;
	}
  }

  const handleUpsellChoices = (choices) => {
    setUpsellChoices(choices);
  }



  return (
    <div>
		<Navbar hideButtons={true} />
		<nav className="bg-white border-bottom border-gray-200 shadow-sm">
          <div className="max-w-screen-xl mx-auto px-3 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center space-x-3">
                <a href="/" className="text-decoration-none">
                  <span className="text-2xl font-semibold text-gray-900 d-flex align-items-center">
				  <img src="/images/logo-icon.png" alt={L.register.chartIconAlt} className="h-8 w-8 mr-2" />
                  <span className="text-dark">ViralGPT</span>
                </span>
              </a>
            </div>
          </div>
        </div>
      </nav>
      {/* Pass the callback down as a prop to PurchaseUsername */}
	  <Container className="justify-content-center text-center  mt-12" fluid>

      <div className="d-flex justify-content-center mb-5 pb-4">
        <div className="position-relative w-75">
          {/* Progress bar background */}
          <div className="progress" style={{height: "2px"}}>
            <div className="progress-bar bg-instagram" role="progressbar"
              style={{width: `${step === 1 ? 25 : step === 2 ? 50 : step === 3 ? 75 : 100}%`}}
              aria-valuenow={step === 1 ? 25 : step === 2 ? 50 : step === 3 ? 75 : 100}
              aria-valuemin="0"
              aria-valuemax="100">
            </div>
          </div>

          {/* Step circles */}
          <div className="position-absolute d-flex justify-content-between w-100" style={{top: "-14px"}}>
            <div className="text-center">
              <div className="rounded-circle border border-2 bg-instagram"
                style={{width: "30px", height: "30px", margin: "0 auto"}}>
                <i className="fas fa-check text-white d-flex justify-content-center align-items-center h-100"></i>
              </div>
              <div className="mt-2 small">{L.register.stepSelectPlan}</div>
            </div>

            <div className="text-center">
              <div className={`rounded-circle border border-2 ${step >= 1 ? 'bg-instagram' : 'bg-white border-secondary'}`}
                style={{width: "30px", height: "30px", margin: "0 auto"}}>
                {step > 2 ? (
                  <i className="fas fa-check text-white d-flex justify-content-center align-items-center h-100"></i>
                ) : (
                  <span className={`d-flex justify-content-center align-items-center h-100 ${step >= 1 ? 'text-white' : 'text-secondary'}`}>2</span>
                )}
              </div>
              <div className="mt-2 small">{L.register.stepSetupAccount}</div>
            </div>

            <div className="text-center">
              <div className={`rounded-circle border border-2 ${step >= 3 ? 'bg-instagram' : 'bg-white border-secondary'}`}
                style={{width: "30px", height: "30px", margin: "0 auto"}}>
                {step > 3 ? (
                  <i className="fas fa-check text-white d-flex justify-content-center align-items-center h-100"></i>
                ) : (
                  <span className={`d-flex justify-content-center align-items-center h-100 ${step >= 3 ? 'text-white' : 'text-secondary'}`}>3</span>
                )}
              </div>
              <div className="mt-2 small">{L.register.stepBoostGrowth}</div>
            </div>

            <div className="text-center">
              <div className={`rounded-circle border border-2 ${step >= 4 ? 'bg-instagram' : 'bg-white border-secondary'}`}
                style={{width: "30px", height: "30px", margin: "0 auto"}}>
                <span className={`d-flex justify-content-center align-items-center h-100 ${step >= 4 ? 'text-white' : 'text-secondary'}`}>4</span>
              </div>
              <div className="mt-2 small">{L.register.stepGetStarted}</div>
            </div>
          </div>
        </div>
      </div>
   
      {step == 1 && <RegisterProfile complete={stepOneComplete} initialEmail={email}/>}

      {/* Step 2: Link social profile */}
      {step == 2 && (
		<RegisterSocial isLoading={isLoading} complete={stepTwoComplete} forced={purchasePlan && interval} email={email} onChangeEmail={handleChangeEmail}/>
	  )}

      {/* Step 3: Boost your growth (upsells) */}
      {step == 3 && profile && !loading && (
        <div>
          <Upsell choice={handleUpsellChoices} step={step}/>
          <div className="d-flex flex-column align-items-center mt-4 mb-5" style={{ maxWidth: 520, margin: '0 auto' }}>
            <button
              onClick={() => setStep(4)}
              className="stripe-btn stripe-btn--primary w-100"
              style={{ padding: '0.85rem', fontSize: '1rem', maxWidth: 520 }}
            >
              {L.register.continueToCheckout}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Checkout */}
      {step == 4 && profile && (
        <div className="flex justify-center">
          <RegisterCheckout
            show={true}
            planName={purchasePlan}
            isAnnual={interval === 'annual'}
            profile={profile}
			firstPackageInterval={firstPackageInterval}
			upsellChoices={upsellChoices}
			isExistingUser={isExistingUser}
			upsellStep={'true'}
			email={email}
			onChangeEmail={handleChangeEmail}
          />
        </div>
      )}
    </Container>

	{step == 4 && profile && !loading && <Upsell choice={handleUpsellChoices} step={step}/>}

	<Testimonials />
	
	<FAQ />
	
	
    </div>
  );
};

export default Register;
